import { type Destination } from '../routes';

/**
 * How each homepage slot is drawn, ported from the 2026-10 mockup.
 *
 * ## Words live in the content schema, treatments live here
 *
 * Every title, sentence, button label and link on the page is a content block
 * declared once in `@jt/platform-content/schema`, with the design's copy as its
 * fallback, so the dashboard can change it. What this file holds is the part an
 * editor must not change: which card is dark and which is violet, whether it
 * holds a phone or a photograph, how a photograph is cropped, and the image the
 * design shipped with. Repeating the copy here as well would give the page two
 * sources for the same sentence, and they would disagree the first time
 * somebody edited one.
 *
 * ## Shipped images
 *
 * The approved artwork from `design_handoff_just_treated_home/assets/`,
 * re-encoded as WebP for the web (61 MB of PNG became about 2 MB) and served
 * from `public/home/`. An image block left empty draws these; an uploaded asset
 * replaces them. Nothing references the handoff folder at runtime.
 *
 * Nothing here is domain logic, and none of it is fetched.
 */

// ---------------------------------------------------------------------------
// Explore Just Treated — six slots, each with its own treatment
// ---------------------------------------------------------------------------

export interface ExploreSlot {
  /** Slot number, which is also the content key: `explore.card{n}-title`. */
  readonly n: number;
  readonly imageKey: string;
  readonly image: string;
  /** Card background: the dark ink or the deep violet. */
  readonly tone: 'midnight' | 'royal';
  /** Button treatment, as the frame draws each one. */
  readonly button: 'violet' | 'glass' | 'white' | 'glass-soft';
  /**
   * `phone` — an app screenshot inside a phone rising from the card's bottom.
   * `cover` — a photograph filling the card.
   * `fade` — a photograph across the lower card, faded into the dark top.
   */
  readonly media: 'phone' | 'cover' | 'fade';
  readonly analyticsKey: string;
}

export const exploreSlots: readonly ExploreSlot[] = [
  {
    n: 1,
    imageKey: 'explore.medicines.image',
    image: '/home/explore/medicines.webp',
    tone: 'midnight',
    button: 'violet',
    media: 'phone',
    analyticsKey: 'service_medicines',
  },
  {
    n: 2,
    imageKey: 'explore.find-businesses.image',
    image: '/home/explore/find-businesses.webp',
    tone: 'royal',
    button: 'glass',
    media: 'cover',
    analyticsKey: 'service_find_businesses',
  },
  {
    n: 3,
    imageKey: 'explore.blood-tests.image',
    image: '/home/explore/blood-tests.webp',
    tone: 'midnight',
    button: 'white',
    media: 'fade',
    analyticsKey: 'service_lab_tests',
  },
  {
    n: 4,
    imageKey: 'explore.nutrition.image',
    image: '/home/explore/nutrition.webp',
    tone: 'royal',
    button: 'glass',
    media: 'phone',
    analyticsKey: 'service_nutrition',
  },
  {
    n: 5,
    imageKey: 'explore.care-homes.image',
    image: '/home/explore/care-homes.webp',
    tone: 'midnight',
    button: 'glass-soft',
    media: 'fade',
    analyticsKey: 'service_care_homes',
  },
  {
    n: 6,
    imageKey: 'explore.consultations.image',
    image: '/home/explore/book-consultations.webp',
    tone: 'royal',
    button: 'glass',
    media: 'phone',
    analyticsKey: 'service_consultations',
  },
];

// ---------------------------------------------------------------------------
// Pharmacy OS — seven tabs over seven product screens
// ---------------------------------------------------------------------------

export interface OsTab {
  readonly slug: string;
  readonly image: string;
  /** What the screenshot shows, for a reader who cannot see it. */
  readonly alt: string;
  /**
   * Drawn as the animated route map rather than a screenshot. The screenshot
   * is still shipped: it is the fallback when motion is reduced, when the
   * animation cannot run, and before the tab has been reached.
   */
  readonly live?: boolean;
}

/**
 * Marketing screenshots of Pharmacy OS, rendered from the design's own source
 * screens. Every name, address and figure in them is invented demonstration
 * data; none of it is read from the platform.
 */
export const osTabs: readonly OsTab[] = [
  { slug: 'dashboard', image: '/home/os/dashboard.webp', alt: 'The Pharmacy OS dashboard' },
  {
    slug: 'demand',
    image: '/home/os/demand-intelligence.webp',
    alt: 'Pharmacy OS demand intelligence and the prescription queue',
  },
  {
    slug: 'compliance',
    image: '/home/os/compliance.webp',
    alt: 'Pharmacy OS compliance — fridge temperatures',
  },
  { slug: 'data', image: '/home/os/pharmacy-data.webp', alt: 'Pharmacy OS pharmacy data intelligence' },
  { slug: 'operations-ai', image: '/home/os/operations-ai.webp', alt: 'Pharmacy OS Operations AI' },
  { slug: 'emr', image: '/home/os/pharmacy-emr.webp', alt: 'Pharmacy OS patient record (EMR)' },
  {
    slug: 'delivery',
    image: '/home/os/pharmacy-delivery.webp',
    alt: 'Pharmacy OS delivery route optimisation, with drivers on three zone routes',
    live: true,
  },
];

// ---------------------------------------------------------------------------
// Solutions for every business — twelve photographed cards
// ---------------------------------------------------------------------------

export interface SolutionSlot {
  readonly slug: string;
  readonly image: string;
  /** Horizontal crop of the photograph, from the frame. */
  readonly focus: string;
}

export const solutionSlots: readonly SolutionSlot[] = [
  { slug: 'pharmacies', image: '/home/solutions/pharmacies.webp', focus: '50% 0%' },
  { slug: 'clinics', image: '/home/solutions/clinics-gps.webp', focus: '68% 50%' },
  { slug: 'beauty', image: '/home/solutions/beauty-aesthetics.webp', focus: '40% 50%' },
  { slug: 'gyms', image: '/home/solutions/gyms-fitness.webp', focus: '40% 50%' },
  { slug: 'dentists', image: '/home/solutions/dentists.webp', focus: '62% 50%' },
  { slug: 'wellness', image: '/home/solutions/wellness-centres.webp', focus: '45% 50%' },
  { slug: 'physiotherapy', image: '/home/solutions/physiotherapy.webp', focus: '55% 50%' },
  { slug: 'private-hospitals', image: '/home/solutions/private-hospitals.webp', focus: '45% 50%' },
  { slug: 'nutrition-weight', image: '/home/solutions/nutrition-weight.webp', focus: '68% 50%' },
  { slug: 'mental-wellbeing', image: '/home/solutions/mental-wellbeing.webp', focus: '66% 50%' },
  { slug: 'care-providers', image: '/home/solutions/care-providers.webp', focus: '55% 50%' },
  { slug: 'brands', image: '/home/solutions/health-wellness-brands.webp', focus: '42% 50%' },
];

// ---------------------------------------------------------------------------
// Partner Portal — five insight cards
// ---------------------------------------------------------------------------

export interface PortalSlot {
  readonly slug: string;
  readonly image: string;
  readonly focus: string;
}

export const portalSlots: readonly PortalSlot[] = [
  { slug: 'sales', image: '/home/portal/latest-sales.webp', focus: '38% 55%' },
  { slug: 'conversion', image: '/home/portal/customer-conversion.webp', focus: '62% 50%' },
  { slug: 'revenue', image: '/home/portal/revenue-trends.webp', focus: '45% 50%' },
  { slug: 'promotions', image: '/home/portal/promotions.webp', focus: '62% 50%' },
  { slug: 'forecast', image: '/home/portal/forecast-stock.webp', focus: '40% 50%' },
];

// ---------------------------------------------------------------------------
// The app screens shown in the purple statement section
// ---------------------------------------------------------------------------

export interface AppRow {
  readonly name: string;
  readonly meta: string;
  readonly chip?: string;
  readonly chipTone?: 'success' | 'violet' | 'warning';
  /** Progress bar width, e.g. `75%`. */
  readonly pct?: string;
}

export interface AppScreen {
  readonly key: string;
  readonly title: string;
  readonly badge: string;
  readonly featureName: string;
  readonly featureCopy: string;
  readonly rows: readonly AppRow[];
}

/**
 * Illustrative app screens.
 *
 * Demonstration content, and labelled as such where it is announced. None of
 * it is real patient data, and none of it comes from an API. The first screen
 * is drawn as the design's full "Your day" layout (see `dayPlan`); its rows
 * here are what a screen reader is given.
 */
export const appScreens: readonly AppScreen[] = [
  {
    key: 'your-day',
    title: 'Your day',
    badge: 'Today',
    featureName: 'Your day',
    featureCopy: 'Everything for today, in one glance.',
    rows: [
      { name: '9:00 AM', meta: 'Medication · next dose' },
      { name: '11:30 AM', meta: 'Dentist appointment' },
      { name: '1:00 PM', meta: 'Lunch · Food AI plan' },
      { name: '6:30 PM', meta: 'Gym · leg day' },
      { name: 'Saturday', meta: "Mum's birthday" },
    ],
  },
  {
    key: 'health',
    title: 'Health & medication',
    badge: 'Synced',
    featureName: 'Health & medication',
    featureCopy: 'Doses, NHS appointments and health monitors — never missed.',
    rows: [
      { name: 'Medication', meta: 'Ramipril · 9:00 AM', chip: 'Taken', chipTone: 'success' },
      { name: 'Repeat prescription', meta: '28 tablets left', chip: 'Reorder', chipTone: 'violet' },
      { name: 'NHS appointment', meta: 'GP review · Thu 10:15 AM', chip: 'Booked', chipTone: 'violet' },
      { name: 'Dentist', meta: 'Check-up · 11:30 AM', chip: 'Today', chipTone: 'warning' },
      { name: 'Health monitor', meta: 'Heart rate · 62 bpm', chip: 'Normal', chipTone: 'success' },
    ],
  },
  {
    key: 'organised',
    title: 'Just Organised',
    badge: 'Family',
    featureName: 'Just Organised',
    featureCopy: 'Personal diary, family plans, lists, trips and birthdays.',
    rows: [
      { name: 'Daily planner', meta: '4 things planned today' },
      { name: 'Mum', meta: 'GP visit · Friday 2:00 PM' },
      { name: 'Shopping list', meta: '12 items · Food AI suggested' },
      { name: 'Trips & holidays', meta: 'Cornwall · 12–16 Oct' },
      { name: 'Birthdays', meta: 'Mum · Saturday' },
    ],
  },
  {
    key: 'wellbeing',
    title: 'Wellbeing & finance',
    badge: 'On track',
    featureName: 'Wellbeing & finance',
    featureCopy: 'Weight, calories, gym and financial planners — on track.',
    rows: [
      { name: 'Gym', meta: '3 of 4 sessions this week', pct: '75%' },
      { name: 'Weight', meta: '74.2 kg · goal 72 kg', pct: '82%' },
      { name: 'Calories tracker', meta: '1,450 of 2,000 kcal', pct: '72%' },
      { name: 'Financial planner', meta: 'Health budget · £86 of £120 left', pct: '28%' },
    ],
  },
];

/** The "Your day" screen's week strip and timeline, from the frame. */
export const dayPlan = {
  place: 'M1 4BT, Manchester',
  week: [
    ['Mon', 15],
    ['Tue', 16],
    ['Wed', 17],
    ['Thu', 18],
    ['Fri', 19],
    ['Sat', 20],
    ['Sun', 21],
  ] as const,
  items: [
    {
      time: '9:00',
      half: 'AM',
      name: 'Medication',
      meta: 'Next dose · 2 tablets · in 15 min',
      icon: 'pill',
      tone: 'sky',
    },
    {
      time: '11:30',
      half: 'AM',
      name: 'Dentist appointment',
      meta: 'City Dental Clinic, Manchester',
      icon: 'tooth',
      tone: 'green',
    },
    { time: '1:00', half: 'PM', name: 'Lunch', meta: 'Food plan · 450 kcal', icon: 'bowl', tone: 'amber' },
    {
      time: '6:30',
      half: 'PM',
      name: 'Gym',
      meta: 'Riverside Fitness · Leg day',
      icon: 'dumbbell',
      tone: 'rose',
    },
    { time: 'All', half: 'day', name: "Mum's birthday", meta: 'Reminder', icon: 'gift', tone: 'violet' },
  ],
} as const;

// ---------------------------------------------------------------------------
// Grow with Just Treated — four partner rows
// ---------------------------------------------------------------------------

export interface GrowSlot {
  readonly slug: string;
  readonly image: string;
  readonly focus: string;
  /** The colour behind the photograph while it loads. */
  readonly tint: 'lavender' | 'deep';
  readonly analyticsKey: string;
}

export const growSlots: readonly GrowSlot[] = [
  {
    slug: 'pharmacy',
    image: '/home/grow/pharmacy.webp',
    focus: '50% 0%',
    tint: 'lavender',
    analyticsKey: 'partner_pharmacy',
  },
  {
    slug: 'clinician',
    image: '/home/grow/clinician.webp',
    focus: '55% 25%',
    tint: 'deep',
    analyticsKey: 'partner_clinician',
  },
  {
    slug: 'courier',
    image: '/home/grow/delivery-courier.webp',
    focus: '46% 30%',
    tint: 'deep',
    analyticsKey: 'partner_courier',
  },
  {
    slug: 'suppliers',
    image: '/home/grow/suppliers.webp',
    focus: '50% 40%',
    tint: 'lavender',
    analyticsKey: 'partner_suppliers',
  },
];

// ---------------------------------------------------------------------------
// Download our apps
// ---------------------------------------------------------------------------

export interface DownloadSlot {
  readonly slug: 'customers' | 'partners';
  readonly imageKey: string;
  readonly image: string;
  readonly focus: string;
}

export const downloadSlots: readonly DownloadSlot[] = [
  { slug: 'customers', imageKey: 'download.image', image: '/home/download/customers.webp', focus: '50% 55%' },
  {
    slug: 'partners',
    imageKey: 'download.partners.image',
    image: '/home/download/partners.webp',
    focus: '40% 50%',
  },
];

// ---------------------------------------------------------------------------
// News
// ---------------------------------------------------------------------------

/**
 * The homepage's news is editorial copy held in the content blocks. There is
 * no news or article module on the platform yet, so there is nothing to pull
 * posts from and these articles have no pages; a headline is plain text until
 * an editor gives it a link.
 */
export const newsFeatured = { image: '/home/news/lab-partnership.webp' } as const;

export interface NewsSlot {
  readonly slug: string;
  readonly image: string;
  readonly focus: string;
}

export const newsSlots: readonly NewsSlot[] = [
  { slug: 'app-update', image: '/home/news/app-update.webp', focus: '82% 50%' },
  { slug: 'communities', image: '/home/news/community.webp', focus: '80% 50%' },
  { slug: 'penny', image: '/home/news/penny-loyalty.webp', focus: '80% 50%' },
];

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export interface NavGroup {
  readonly key: string;
  readonly title: string;
  readonly links: readonly Destination[];
}
